import { InjectionToken, inject } from '@angular/core';
import {
  ChatClient, Heartbeat, Notifier,
  mapConversationRow, mapMessageRow, mapParticipantRow,
} from '@afs/chat-core';
import type {
  ChatMessage, Conversation, DesktopPermission, HeartbeatEnv, NotifyEnv, Participant, Row,
  SupabaseLike,
} from '@afs/chat-core';
import { DATA_CLIENT } from './auth.service';

/** A conversation this user is in, with the flag that decides which list it lands in. */
export interface MyConversation {
  conversation: Conversation;
  /** True when this person handed the chat over and can only read it. */
  watching: boolean;
}

export interface ChatRuntime {
  createClient(userId: string): ChatClient;
  createNotifier(): Notifier;
  createHeartbeat(hooks: {
    sendHeartbeat: () => void;
    sendOffDutyBeacon: () => void;
  }): Heartbeat;
  loadMyConversations(userId: string): Promise<MyConversation[]>;
  loadMessages(conversationId: string): Promise<ChatMessage[]>;
  loadParticipants(conversationId: string): Promise<Participant[]>;
}

/** Shipped in public/, so the runtime path is site-root absolute. A CDN URL would
 *  be blocked by the CSP and the alert would be silent with no console error. */
const SOUND = '/chat/new-chat.mp3';

/** Chat:HeartbeatSeconds and Chat:IdleMinutes, as milliseconds. */
const HEARTBEAT_MS = 45_000;
const IDLE_MS = 15 * 60_000;
const TITLE_FLASH_MS = 1_000;

/** `visibilitychange` is fired at the document and does not bubble to the window;
 *  `pointerdown`, `keydown`, `pagehide` and `focus` belong to the window. Wiring
 *  all of them to the window compiles and then silently never reports a hidden
 *  tab, so a background tab would beat "on duty" forever. */
function eventTarget(type: string): EventTarget {
  return type === 'visibilitychange' ? document : window;
}

export function browserHeartbeatEnv(): HeartbeatEnv {
  return {
    now: () => Date.now(),
    isHidden: () => document.hidden,
    setInterval: (fn, ms) => window.setInterval(fn, ms),
    clearInterval: (id) => window.clearInterval(id),
    addEventListener: (type, fn) => eventTarget(type).addEventListener(type, fn as EventListener),
    removeEventListener: (type, fn) =>
      eventTarget(type).removeEventListener(type, fn as EventListener),
  };
}

export function browserNotifyEnv(): NotifyEnv {
  return {
    // Every browser call below swallows its own failure. An alert that cannot be
    // delivered must never take down the message handler that asked for it — the
    // message has already arrived and still has to be rendered.
    playSound: (name) => {
      try {
        void new Audio(name).play().catch(() => undefined);
      } catch {
        /* no audio output, a blocked asset, or a stubbed-out play() */
      }
    },

    primeAudio: () => {
      try {
        const audio = new Audio(SOUND);
        audio.muted = true;
        void audio.play().then(() => audio.pause()).catch(() => undefined);
      } catch {
        /* nothing to prime */
      }
    },

    showDesktop: (title, body, tag) => {
      try {
        // Deliberately no separate guard for a missing Notification API: some
        // Android WebViews ship none, and `new undefined()` is a TypeError this
        // catch already turns into silence. A second check returning the same
        // nothing would be dead code no test could kill. The tag makes a second
        // alert for one conversation replace the first rather than stacking.
        new Notification(title, { body, tag });
      } catch {
        /* no Notification API, or permission revoked between check and call */
      }
    },

    requestPermission: async (): Promise<DesktopPermission> => {
      try {
        // Same reasoning as showDesktop, plus a browser that refuses the prompt
        // outside a user gesture: every one of those ends as 'denied' here.
        return await Notification.requestPermission() as DesktopPermission;
      } catch {
        return 'denied';
      }
    },

    isHidden: () => document.hidden,
    getTitle: () => document.title,
    setTitle: (value) => { document.title = value; },
    addEventListener: (type, fn) => eventTarget(type).addEventListener(type, fn as EventListener),
    removeEventListener: (type, fn) =>
      eventTarget(type).removeEventListener(type, fn as EventListener),
    setInterval: (fn, ms) => window.setInterval(fn, ms),
    clearInterval: (id) => window.clearInterval(id),

    get permission(): DesktopPermission {
      // Read fresh on every notify, and read defensively: reading .permission off
      // undefined throws, and a getter has nowhere to report that.
      const api = (globalThis as { Notification?: typeof Notification }).Notification;
      return api === undefined ? 'denied' : api.permission as DesktopPermission;
    },
  };
}

/**
 * The one place the survey app touches Supabase, window, document, Audio or
 * Notification for chat. SurveyChatService's tests replace this token with an
 * object literal, which is what keeps them fast and free of DOM faking.
 */
export const CHAT_RUNTIME = new InjectionToken<ChatRuntime>('CHAT_RUNTIME', {
  providedIn: 'root',
  factory: (): ChatRuntime => {
    const supabase = inject(DATA_CLIENT);

    return {
      // 'staff' and not 'customer': every survey user is staff (spec section 2,
      // decision 2), and a 'customer' client throws on openPresence. They still
      // do not open the queue — that is the service's choice, made once in Task 3.
      // The cast is structural only: SupabaseLike models a PostgREST builder as the
      // promise it resolves to, while the real builder is a thenable with extra
      // generics, so the two describe the same object and neither is assignable to
      // the other.
      createClient: (userId) =>
        new ChatClient(supabase as unknown as SupabaseLike, { userId, role: 'staff' }),

      createNotifier: () => new Notifier(
        browserNotifyEnv(),
        window.localStorage,
        { soundName: SOUND, titleFlashMs: TITLE_FLASH_MS },
      ),

      // sendHeartbeat goes through ChatClient, which calls afs_chat_touch_presence().
      // Never a direct write to afs_chat_availability: break_until is a service-role
      // column, set only through POST /api/v2/chat/break.
      createHeartbeat: (hooks) => new Heartbeat(browserHeartbeatEnv(), {
        intervalMs: HEARTBEAT_MS,
        idleMs: IDLE_MS,
        sendHeartbeat: hooks.sendHeartbeat,
        sendOffDutyBeacon: hooks.sendOffDutyBeacon,
      }),

      loadMyConversations: async (userId) => {
        // RLS already limits this to conversations the caller is in, so the
        // participant join is here for `watching`, not for security. A failed
        // query leaves data null, which is the same empty answer as no rows —
        // a loader that threw would take out whoever asked.
        const { data } = await supabase
          .from('afs_chat_participants')
          .select('watching, afs_chat_conversations(*)')
          .eq('user_id', userId);

        return ((data ?? []) as Row[])
          // Null when the conversation itself is not readable; mapConversationRow
          // would throw on it.
          .filter((row) => row['afs_chat_conversations'] != null)
          .map((row) => ({
            conversation: mapConversationRow(row['afs_chat_conversations'] as Row),
            watching: row['watching'] === true,
          }));
      },

      loadMessages: async (conversationId) => {
        const { data } = await supabase
          .from('afs_chat_messages')
          .select('*')
          .eq('conversation_id', conversationId)
          .order('created_at', { ascending: true });

        return ((data ?? []) as Row[]).map(mapMessageRow);
      },

      loadParticipants: async (conversationId) => {
        const { data } = await supabase
          .from('afs_chat_participants')
          .select('*')
          .eq('conversation_id', conversationId);

        return ((data ?? []) as Row[]).map(mapParticipantRow);
      },
    };
  },
});
